import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ZoneModal from 'components/ZoneModal'
import Button from 'components/Button'
import Icon from 'components/Icon'
import {
  selectIsLoadingZones,
  selectAvailableZones,
  setIsInitialized,
  addAlert,
  fetchZones,
  selectAllGroups,
  fetchGroups,
} from 'store/global'
import { selectDisplayedZone, setDisplayedZone, closeDisplayedZone, submitZone, deleteZone } from 'store/organizations'
import messages from './messages'
import styles from './Zones.module.scss'
import { orderBy } from 'lodash'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Loader from 'components/Loader/Loader'
import { usePermissions } from 'hooks/usePermissions'
import Search from 'UI/Search/Search'
import { Table, Empty, Button as AntButton } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'

export default function Zones() {
  const dispatch = useDispatch()
  const permissions = usePermissions()
  const isLoading = useSelector(selectIsLoadingZones)
  const allZones = useSelector(selectAvailableZones)
  const displayedZone = useSelector(selectDisplayedZone)
  const allGroups = useSelector(selectAllGroups)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    dispatch(closeDisplayedZone())
    dispatch(setIsInitialized(true))
    dispatch(fetchZones())
  }, [])

  useEffect(() => {
    if (!allGroups || allGroups.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, allGroups])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredZones = React.useMemo(() => {
    if (!allZones || !Array.isArray(allZones)) return []
    if (!searchTerm) return allZones
    return allZones.filter(zone =>
      Object.values(zone).some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase())),
    )
  }, [allZones, searchTerm])

  const sortedZones = React.useMemo(() => {
    if (!sortColumn) return filteredZones
    return orderBy(filteredZones, [sortColumn], [sortOrder])
  }, [filteredZones, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingZone = {}) => {
    const newZone = {
      ...existingZone,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedZone({ zone: newZone }))
  }

  const flattenGroupsForOptions = (groups, level = 0) => {
    let allGroups = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const onSubmit = async zone => {
    try {
      if (displayedZone.action === 'delete') {
        await dispatch(deleteZone({ zoneId: displayedZone.id }))
      } else {
        await dispatch(submitZone({ zone }))
      }

      dispatch(addAlert({ type: 'success', message: 'Zone Action Performed' }))
      dispatch(setDisplayedZone())
    } catch (error) {
      dispatch(addAlert({ type: 'error', message: 'Zone Action Failed' }))
    }
  }

  const onExportCSV = () => {
    if (!sortedZones.length) return

    const exportData = sortedZones.map(zone => {
      const groupNames = (zone.groups || [])
        .map(group => {
          const foundGroup = allGroups?.find(g => g.path === group)
          return foundGroup ? foundGroup.path : group
        })
        .join(', ')

      return {
        id: zone.id || '',
        name: zone.name || '',
        groups: groupNames,
        verticals: zone.verticals || '',
      }
    })

    const fields = ['id', 'name', 'groups', 'verticals']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'zones.csv')
  }

  const tableColumns = [
    {
      title: <FormattedMessage {...messages.columns.id} />,
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortColumn === 'id' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('id'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.name} />,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortColumn === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('name'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.groups} />,
      key: 'groups',
      render: (_, record) => {
        const groups = record.groups || []
        if (groups.length === 0) return ''

        if (!allGroups) return ''
        const flattenedGroups = flattenGroupsForOptions(allGroups)
        const availableGroups = groups.filter(group => {
          return flattenedGroups.some(flattenedGroup => flattenedGroup.path === group)
        })
        return availableGroups.join(', ')
      },
    },
    {
      title: <FormattedMessage {...messages.columns.actions} />,
      key: 'actions',
      render: (_, record) => {
        return (
          <div className={styles.actions}>
            <div onClick={() => onDisplayModal('delete', record)} className={styles.action}>
              <DeleteOutlined />
            </div>
            <div onClick={() => onDisplayModal('edit', record)} className={styles.action}>
              <EditOutlined />
            </div>
          </div>
        )
      },
    },
  ]

  if (isLoading || !allGroups) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading zones and groups...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerActions}>
        {permissions.admin && (
          <AntButton onClick={() => onDisplayModal('create')}>
            <PlusOutlined /> New Zone
          </AntButton>
        )}
        <div className={styles.headerActionsRight}>
          <Search
            placeholder='Search for...'
            enterButton
            allowClear
            style={{ width: 264 }}
            onSearch={value => setSearchTerm(value)}
          />
          <AntButton onClick={onExportCSV} disabled={!sortedZones.length} type='primary'>
            <UploadOutlined /> Export CSV
          </AntButton>
        </div>
      </div>

      <Table
        columns={tableColumns}
        dataSource={sortedZones}
        loading={isLoading}
        rowKey='id'
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: <Empty description='No zones found' />,
        }}
      />

      {displayedZone.isOpen && <ZoneModal mode={displayedZone.action} onSubmit={onSubmit} />}
    </div>
  )
}
